import { useState } from 'react';
import { ChartColumn, Trophy } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, Tabs } from '@heroui/react';
import {
  dashboardKeys,
  getDashboardStats,
  type LeaderboardPeriod,
} from '../../../api/dashboard';
import { PageHeader } from '../../../components/page/page-header';
import { primaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { CondominiumRankingSection } from '../components/condominium-ranking-section';
import { PeriodToggle } from '../components/period-toggle';
import { StatsCards } from '../components/stats-cards';

export default function DashboardPage() {
  const [selectedTab, setSelectedTab] = useState('ranking');
  const [statsPeriod, setStatsPeriod] = useState<LeaderboardPeriod>('all');

  const statsQuery = useQuery({
    queryKey: dashboardKeys.stats(statsPeriod),
    queryFn: () => getDashboardStats(statsPeriod),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Início"
        description="Veja as estatísticas e o ranking de uso de vagas."
      />

      <Tabs selectedKey={selectedTab} onSelectionChange={(key) => setSelectedTab(String(key))}>
        <Tabs.ListContainer>
          <Tabs.List aria-label="Seções do início" className="p-1.5" data-tour="dashboard-tabs">
            <Tabs.Tab id="ranking" className={primaryPageTabClassName}>
              <span className="inline-flex items-center gap-1.5 sm:gap-2">
                <Trophy size={16} className="shrink-0 sm:size-[18px]" />
                Ranking
              </span>
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="stats" className={primaryPageTabClassName} aria-label="Minhas estatísticas">
              <span className="inline-flex items-center gap-1.5 sm:gap-2">
                <ChartColumn size={16} className="shrink-0 sm:size-[18px]" />
                <span className="sm:hidden">Estatísticas</span>
                <span className="hidden sm:inline">Minhas estatísticas</span>
              </span>
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>

        <Tabs.Panel id="ranking" className="pt-3" data-tour="dashboard-ranking">
          <CondominiumRankingSection />
        </Tabs.Panel>

        <Tabs.Panel id="stats" className="pt-3">
          <div className="flex flex-col gap-4">
            <PeriodToggle period={statsPeriod} onPeriodChange={setStatsPeriod} />
            {statsQuery.isError ? (
              <StatusCallout
                status="danger"
                title="Erro ao carregar estatísticas"
                action={
                  <Button size="sm" variant="secondary" onPress={() => statsQuery.refetch()}>
                    Tentar novamente
                  </Button>
                }
              >
                Não foi possível carregar seus números. Tente novamente em instantes.
              </StatusCallout>
            ) : (
              <StatsCards stats={statsQuery.data} isLoading={statsQuery.isLoading} />
            )}
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
